import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { t } from '../../i18n';
import { formatCHF, formatQuotaPercent, parseDecimalInput } from '@werkdelta/shared';
import { Feld, Fehler, InfoTip, RechtsHinweis } from '../../components/ui';
import { useEntityActions } from '../../hooks';
import { api, ApiError } from '../../api';
import type { StepProps } from '../CasePage';

const EVIDENCE_LEVELS = ['A', 'B', 'C', 'D', 'E'] as const;
const STRANDS = ['GESAMT', 'TECHNISCH', 'OPTISCH'] as const;

/**
 * Schritt 8: Zielbaum ausfüllen (u), q festlegen (Evidenzstufe), Berechnung.
 * Kerntrennung (Sperre 15.1): Der Zielbaum erzeugt nie einen Geldbetrag —
 * q ist eine getrennte, begründete Eingabe; q = u nur per Opt-in (Stufe D).
 */
export function Step8Berechnung({ me, caseId, data, reload, readonly }: StepProps) {
  const [vmId, setVmId] = useState<string>(data.valuationModels[0]?.id ?? '');
  const vm = data.valuationModels.find((m: any) => m.id === vmId);

  return (
    <>
      <section className="karte">
        <h2>{t('step8.title')}</h2>
        {data.valuationModels.length === 0 ? (
          <div className="leer-hinweis">{t('step8.noValuationModel')}</div>
        ) : (
          <Feld label={t('step7.valuationModel')} required>
            <select value={vmId} onChange={(e) => setVmId(e.target.value)}>
              {data.valuationModels.map((m: any) => (
                <option key={m.id} value={m.id}>
                  {m.referenceObject ?? m.id.slice(0, 8)}
                  {m.priceRelevant ? ` — P ${formatCHF(m.priceRelevant)}` : ''}
                </option>
              ))}
            </select>
          </Feld>
        )}
      </section>
      {vm && (
        <>
          <BewertungKarte
            me={me}
            caseId={caseId}
            data={data}
            reload={reload}
            readonly={readonly}
            vm={vm}
          />
          <QKarte caseId={caseId} data={data} reload={reload} readonly={readonly} vm={vm} />
          <QVergleichKarte caseId={caseId} vm={vm} />
          <BerechnungKarte caseId={caseId} readonly={readonly} vm={vm} reload={reload} />
        </>
      )}
    </>
  );
}

/* ————— Zielbaum-Bewertung ————— */

function BewertungKarte({
  me,
  caseId,
  data,
  reload,
  readonly,
  vm,
}: StepProps & { vm: any }) {
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [strand, setStrand] = useState<string>('GESAMT');
  const assessments = vm.assessments ?? [];
  const [activeId, setActiveId] = useState<string>(assessments[0]?.id ?? '');
  const actions = useEntityActions(caseId, 'assessments', reload);

  useEffect(() => {
    api
      .get<any[]>('/api/v1/tree-templates')
      .then((list) => {
        setTemplates(list);
        const first = list[0]?.versions?.[0]?.id;
        if (first) setSelectedVersion(first);
      })
      .catch(() => setTemplates([]));
  }, []);

  useEffect(() => {
    setActiveId(assessments[0]?.id ?? '');
  }, [vm.id, assessments.length]);

  async function createAssessment(e: FormEvent) {
    e.preventDefault();
    await actions.create({
      valuationModelId: vm.id,
      strand,
      templateVersionId: selectedVersion,
    });
  }

  return (
    <section className="karte">
      <h2>
        {t('step8.assessment')}
        <InfoTip termKey="nutzwerterfuellung" />
        <InfoTip termKey="gebrauchswert" />
        <InfoTip termKey="geltungswert" />
      </h2>
      {assessments.length === 0 ? (
        <>
          <div className="leer-hinweis">{t('step8.assessmentEmpty')}</div>
          {!readonly && (
            <form onSubmit={createAssessment} style={{ marginTop: '0.8rem' }}>
              <div className="feld-zeile">
                <Feld label={t('step8.template')} required hint={t('tree.justificationRequired')}>
                  <select
                    value={selectedVersion}
                    onChange={(e) => setSelectedVersion(e.target.value)}
                    required
                  >
                    {templates.flatMap((tpl) =>
                      tpl.versions.map((v: any) => (
                        <option key={v.id} value={v.id}>
                          {tpl.name} · {v.version}
                          {v.demoLabel ? ' — DEMO' : ''}
                        </option>
                      )),
                    )}
                  </select>
                </Feld>
                <Feld label={t('step8.strand')} required hint={t('step8.strandHint')}>
                  <select value={strand} onChange={(e) => setStrand(e.target.value)}>
                    {STRANDS.map((s) => (
                      <option key={s} value={s}>
                        {t(`strand.${s}`)}
                      </option>
                    ))}
                  </select>
                </Feld>
              </div>
              <Fehler code={actions.errorCode} />
              <button type="submit" className="knopf-primaer">
                {t('step8.createAssessment')}
              </button>
            </form>
          )}
        </>
      ) : (
        <>
          {assessments.length > 1 && (
            <Feld label={t('step8.assessmentSelect')}>
              <select value={activeId} onChange={(e) => setActiveId(e.target.value)}>
                {assessments.map((a: any) => (
                  <option key={a.id} value={a.id}>
                    {t(`strand.${a.strand}`)} · {a.status === 'ABGESCHLOSSEN' ? t('step8.completed') : t('step8.draft')}
                  </option>
                ))}
              </select>
            </Feld>
          )}
          {activeId && (
            <BaumEditor
              key={activeId}
              me={me}
              caseId={caseId}
              assessmentId={activeId}
              readonly={readonly}
              data={data}
              reload={reload}
            />
          )}
          {!readonly && (
            <details style={{ marginTop: '0.8rem' }}>
              <summary>{t('step8.addAnotherAssessment')}</summary>
              <form onSubmit={createAssessment} style={{ marginTop: '0.6rem' }}>
                <div className="feld-zeile">
                  <Feld label={t('step8.template')} required>
                    <select value={selectedVersion} onChange={(e) => setSelectedVersion(e.target.value)}>
                      {templates.flatMap((tpl) =>
                        tpl.versions.map((v: any) => (
                          <option key={v.id} value={v.id}>
                            {tpl.name} · {v.version}
                          </option>
                        )),
                      )}
                    </select>
                  </Feld>
                  <Feld label={t('step8.strand')} required>
                    <select value={strand} onChange={(e) => setStrand(e.target.value)}>
                      {STRANDS.map((s) => (
                        <option key={s} value={s}>
                          {t(`strand.${s}`)}
                        </option>
                      ))}
                    </select>
                  </Feld>
                </div>
                <button type="submit" className="knopf-primaer knopf-klein">
                  {t('step8.createAssessment')}
                </button>
              </form>
            </details>
          )}
        </>
      )}
    </section>
  );
}

interface FlatNode {
  key: string;
  label: string;
  definition: string;
  scale?: string;
  depth: number;
  isLeaf: boolean;
  templateWeight: string;
}

function flatten(nodes: any[], depth = 0): FlatNode[] {
  const out: FlatNode[] = [];
  for (const n of nodes) {
    out.push({
      key: n.key,
      label: n.label,
      definition: n.definition,
      scale: n.scale,
      depth,
      isLeaf: !n.children || n.children.length === 0,
      templateWeight: n.weightPercent,
    });
    if (n.children) out.push(...flatten(n.children, depth + 1));
  }
  return out;
}

function BaumEditor({
  me,
  caseId,
  assessmentId,
  readonly,
  data,
  reload,
}: {
  me: StepProps['me'];
  caseId: string;
  assessmentId: string;
  readonly: boolean;
  data: any;
  reload: () => void;
}) {
  const [detail, setDetail] = useState<any | null>(null);
  const [values, setValues] = useState<
    Record<
      string,
      { weight: string; e: string; just: string; notAssessed: boolean; assumption: boolean }
    >
  >({});
  const [missing, setMissing] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [safetyConfirm, setSafetyConfirm] = useState(false);

  const load = () =>
    api
      .get(`/api/v1/cases/${caseId}/assessments/${assessmentId}`)
      .then((d) => {
        setDetail(d);
        const map: Record<
          string,
          { weight: string; e: string; just: string; notAssessed: boolean; assumption: boolean }
        > = {};
        for (const node of d.assessment.nodes) {
          map[node.nodeKey] = {
            weight: node.weightPercent,
            e: node.fulfillment ?? '',
            just: node.justification ?? '',
            notAssessed: Boolean(node.notAssessed),
            assumption: Boolean(node.assumptionBased),
          };
        }
        setValues(map);
      })
      .catch((e) => setError(e instanceof ApiError ? e.code : 'INTERNAL'));

  useEffect(() => {
    void load();
  }, [assessmentId]); // load ist stabil genug: assessmentId ist der einzige Input

  const flat = useMemo(() => (detail ? flatten(detail.template.nodes) : []), [detail]);

  if (error) return <Fehler code={error} />;
  if (!detail) return <p>{t('app.loading')}</p>;

  const completed = detail.assessment.status === 'ABGESCHLOSSEN';
  const editable = !readonly && !completed;
  const computation = detail.computation;

  // Sicherheitsregel 3.5: betrifft dieser Strang sicherheitsrelevante Mängel?
  const safetyRelevant =
    detail.assessment.strand === 'OPTISCH' &&
    data.defectFindings.some((d: any) => d.safetyRelevant || d.structuralSafetyRelevant);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const nodes = flat.map((n) => {
        const v = values[n.key];
        const notAssessed = n.isLeaf ? Boolean(v?.notAssessed) || !v?.e : false;
        return {
          nodeKey: n.key,
          weightPercent: v?.weight ?? n.templateWeight,
          // Kein stilles 1.0: entweder Erfüllungsgrad oder «nicht bewertet».
          fulfillment: n.isLeaf && !notAssessed ? v?.e || undefined : undefined,
          notAssessed: n.isLeaf ? notAssessed : undefined,
          assumptionBased: n.isLeaf && !notAssessed ? Boolean(v?.assumption) : undefined,
          justification: v?.just || undefined,
        };
      });
      const res = await api.put(`/api/v1/cases/${caseId}/assessments/${assessmentId}/nodes`, {
        nodes,
      });
      setMissing(res.missingJustifications);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'INTERNAL');
    } finally {
      setSaving(false);
    }
  }

  async function complete() {
    setError(null);
    try {
      await api.post(`/api/v1/cases/${caseId}/assessments/${assessmentId}/complete`, {
        safetyConfirmation: safetyConfirm || undefined,
      });
      await load();
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'INTERNAL');
    }
  }

  function setValue(
    key: string,
    patch: Partial<{
      weight: string;
      e: string;
      just: string;
      notAssessed: boolean;
      assumption: boolean;
    }>,
  ) {
    setValues((v) => ({
      ...v,
      [key]: {
        weight: v[key]?.weight ?? '',
        e: v[key]?.e ?? '',
        just: v[key]?.just ?? '',
        notAssessed: v[key]?.notAssessed ?? false,
        assumption: v[key]?.assumption ?? false,
        ...patch,
      },
    }));
  }

  return (
    <>
      {detail.template.demoLabel && <p className="demo-banner">{detail.template.demoLabel}</p>}
      <p className="meta">
        {detail.template.name} · {t('common.version')} {detail.template.version} ·{' '}
        {t(`strand.${detail.assessment.strand}`)}
        {completed && ` · ${t('step8.completed')}`}
      </p>
      <p className="erklaerung">{t('tree.justificationRequired')}</p>
      <div style={{ overflowX: 'auto' }}>
        <table className="daten">
          <thead>
            <tr>
              <th>{t('step8.node')}</th>
              <th className="zahl">{t('step8.weight')}</th>
              <th className="zahl">{t('step8.fulfillment')}</th>
              <th>{t('common.justification')}</th>
            </tr>
          </thead>
          <tbody>
            {flat.map((n) => {
              const v = values[n.key] ?? {
                weight: n.templateWeight,
                e: '',
                just: '',
                notAssessed: false,
                assumption: false,
              };
              const deviates =
                v.weight !== n.templateWeight ||
                (n.isLeaf && !v.notAssessed && v.e !== '' && v.e !== '1');
              return (
                <tr key={n.key}>
                  <td>
                    <span style={{ paddingLeft: `${n.depth * 1.2}rem` }}>
                      {n.isLeaf ? '' : '▸ '}
                      <strong>{n.label}</strong>
                    </span>
                    <p className="meta" style={{ margin: 0, paddingLeft: `${n.depth * 1.2}rem` }}>
                      {n.definition}
                    </p>
                    {n.scale && (
                      <details style={{ paddingLeft: `${n.depth * 1.2}rem` }}>
                        <summary className="meta">{t('step8.scale')}</summary>
                        <p className="meta">{n.scale}</p>
                      </details>
                    )}
                  </td>
                  <td className="zahl" style={{ width: '7.5rem' }}>
                    <input
                      aria-label={`${t('step8.weight')} ${n.label}`}
                      value={v.weight}
                      onChange={(e) => setValue(n.key, { weight: e.target.value })}
                      disabled={!editable}
                      style={{ width: '6.5rem', textAlign: 'right' }}
                    />
                  </td>
                  <td className="zahl" style={{ width: '9.5rem' }}>
                    {n.isLeaf ? (
                      <>
                        <input
                          aria-label={`${t('step8.fulfillment')} ${n.label}`}
                          value={v.notAssessed ? '' : v.e}
                          onChange={(e) => setValue(n.key, { e: e.target.value, notAssessed: false })}
                          disabled={!editable || v.notAssessed}
                          placeholder={v.notAssessed ? t('step8.notAssessed') : ''}
                          style={{ width: '6.5rem', textAlign: 'right' }}
                        />
                        <label
                          className="meta"
                          style={{
                            display: 'flex',
                            gap: '0.3rem',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            marginTop: '0.25rem',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={v.notAssessed}
                            onChange={(e) =>
                              setValue(n.key, {
                                notAssessed: e.target.checked,
                                e: e.target.checked ? '' : v.e,
                                assumption: e.target.checked ? false : v.assumption,
                              })
                            }
                            disabled={!editable}
                            aria-label={`${t('step8.notAssessed')} ${n.label}`}
                          />
                          {t('step8.notAssessed')}
                        </label>
                        {!v.notAssessed && (
                          <label
                            className="meta"
                            style={{
                              display: 'flex',
                              gap: '0.3rem',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={v.assumption}
                              onChange={(e) => setValue(n.key, { assumption: e.target.checked })}
                              disabled={!editable}
                              aria-label={`${t('step8.assumption')} ${n.label}`}
                            />
                            {t('step8.assumption')}
                          </label>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <input
                      aria-label={`${t('common.justification')} ${n.label}`}
                      value={v.just}
                      onChange={(e) => setValue(n.key, { just: e.target.value })}
                      disabled={!editable}
                      placeholder={deviates ? t('step8.justificationNeeded') : ''}
                      style={{ width: '100%' }}
                    />
                    {missing.includes(n.key) && (
                      <p className="feld-fehler">{t('step8.justificationMissing')}</p>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {computation?.ok === false && (
        <div className="fehlermeldung" role="alert">
          <ul style={{ margin: 0 }}>
            {computation.errors.map((err: any, i: number) => (
              <li key={i}>{t(`calc.error.${err.code}`, err.params)}</li>
            ))}
          </ul>
        </div>
      )}
      {computation?.ok && (
        <p style={{ fontSize: '1.15rem' }}>
          <strong>
            u = {computation.value.u}{' '}
            <span className="meta">({formatQuotaPercent(computation.value.u)})</span>
          </strong>{' '}
          <InfoTip termKey="nutzwerterfuellung" />
        </p>
      )}
      {computation?.ok && Number(computation.value.coverage ?? '1') < 1 && (
        <p className="hinweis" role="status">
          {t('step8.coverage')}: {formatQuotaPercent(computation.value.coverage)} —{' '}
          {t('step8.unassessedNote', {
            count: String(computation.value.unassessedLeaves?.length ?? 0),
          })}
        </p>
      )}
      <p className="hinweis">{t('step8.uIsNotMoney')}</p>

      <Fehler code={error} />
      {editable && (
        <div className="aktionen">
          <button type="button" className="knopf-primaer" onClick={save} disabled={saving}>
            {t('step8.saveTree')}
          </button>
          {(me.role === 'GUTACHTER' || me.role === 'ADMIN') && (
            <>
              {safetyRelevant && (
                <div className="kontrollzeile" style={{ maxWidth: '38rem' }}>
                  <input
                    id="safety-confirm"
                    type="checkbox"
                    checked={safetyConfirm}
                    onChange={(e) => setSafetyConfirm(e.target.checked)}
                  />
                  <label htmlFor="safety-confirm">{t('safety.confirmation.text')}</label>
                </div>
              )}
              <button type="button" className="knopf-sekundaer" onClick={complete}>
                {t('step8.completeAssessment')}
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

/* ————— Wertrelation q ————— */

function leerRow() {
  return {
    faktor: '',
    feststellung: '',
    ausgangswert: '',
    vergleichsdaten: '',
    unterschiede: '',
    zuAbschlaege: '',
    abschlag: '',
    beleg: '',
    gewicht: '',
    belegqualitaet: '',
    unsicherheitswirkung: '',
    begruendung: '',
    // K4a (D-031): optionale Bandgrenzen des Faktorabschlags (all-or-none je Matrix)
    abschlagMin: '',
    abschlagMax: '',
    // D-035: Herkunft aus einem übernommenen Referenzfall (für das DEMO-Gate)
    refId: '',
    refDataClass: '',
  };
}
type MatrixRowForm = ReturnType<typeof leerRow>;

/** Live-Intervall (Vorschau) — verbindlich rechnet der Server (calc-core). */
function liveBand(rows: MatrixRowForm[]): { qMin: number; qMax: number } | null {
  if (rows.length === 0) return null;
  if (!rows.every((r) => r.abschlagMin.trim() !== '' && r.abschlagMax.trim() !== '')) return null;
  let lo = 0;
  let hi = 0;
  for (const r of rows) {
    const g = (Number(r.gewicht) || 0) / 100;
    lo += g * (Number(r.abschlagMin) || 0);
    hi += g * (Number(r.abschlagMax) || 0);
  }
  return { qMin: 1 - hi, qMax: 1 - lo };
}

/** Eine Matrixzeile (q- und Restmangel-Matrix nutzen dieselbe Herleitungsstruktur). */
function MatrixZeile({
  index,
  row,
  onPatch,
  onRemove,
}: {
  index: number;
  row: MatrixRowForm;
  onPatch: (patch: Partial<MatrixRowForm>) => void;
  onRemove: () => void;
}) {
  return (
    <fieldset className="matrix-row">
      <legend>
        {t('report.matrix.faktor')} {index + 1}
        <button type="button" className="knopf-sekundaer knopf-klein" onClick={onRemove}>
          {t('common.delete')}
        </button>
      </legend>
      <div className="feld-zeile">
        <Feld label={t('report.matrix.faktor')}>
          <input value={row.faktor} onChange={(e) => onPatch({ faktor: e.target.value })} />
        </Feld>
        <Feld label={t('report.matrix.feststellung')}>
          <input value={row.feststellung} onChange={(e) => onPatch({ feststellung: e.target.value })} />
        </Feld>
      </div>
      <div className="feld-zeile">
        <Feld label={t('report.matrix.ausgangswert')}>
          <input value={row.ausgangswert} onChange={(e) => onPatch({ ausgangswert: e.target.value })} />
        </Feld>
        <Feld label={t('report.matrix.vergleichsdaten')}>
          <input value={row.vergleichsdaten} onChange={(e) => onPatch({ vergleichsdaten: e.target.value })} />
        </Feld>
      </div>
      <div className="feld-zeile">
        <Feld label={t('report.matrix.unterschiede')}>
          <input value={row.unterschiede} onChange={(e) => onPatch({ unterschiede: e.target.value })} />
        </Feld>
        <Feld label={t('report.matrix.zuAbschlaege')}>
          <input value={row.zuAbschlaege} onChange={(e) => onPatch({ zuAbschlaege: e.target.value })} />
        </Feld>
      </div>
      <div className="feld-zeile">
        <Feld label={t('report.matrix.faktorabschlag')} hint="0–1">
          <input value={row.abschlag} onChange={(e) => onPatch({ abschlag: e.target.value })} style={{ textAlign: 'right' }} />
        </Feld>
        <Feld label={t('report.matrix.relevanz')} hint="%">
          <input value={row.gewicht} onChange={(e) => onPatch({ gewicht: e.target.value })} style={{ textAlign: 'right' }} />
        </Feld>
        <Feld label={t('report.matrix.beleg')}>
          <input value={row.beleg} onChange={(e) => onPatch({ beleg: e.target.value })} />
        </Feld>
      </div>
      <div className="feld-zeile">
        <Feld label={t('step8.matrixMin')} hint="0–1">
          <input value={row.abschlagMin} onChange={(e) => onPatch({ abschlagMin: e.target.value })} style={{ textAlign: 'right' }} />
        </Feld>
        <Feld label={t('step8.matrixMax')} hint="0–1">
          <input value={row.abschlagMax} onChange={(e) => onPatch({ abschlagMax: e.target.value })} style={{ textAlign: 'right' }} />
        </Feld>
      </div>
      <div className="feld-zeile">
        <Feld label={t('report.matrix.belegqualitaet')}>
          <input value={row.belegqualitaet} onChange={(e) => onPatch({ belegqualitaet: e.target.value })} />
        </Feld>
        <Feld label={t('report.matrix.unsicherheitswirkung')}>
          <input value={row.unsicherheitswirkung} onChange={(e) => onPatch({ unsicherheitswirkung: e.target.value })} />
        </Feld>
      </div>
      <Feld label={t('report.matrix.begruendung')}>
        <input value={row.begruendung} onChange={(e) => onPatch({ begruendung: e.target.value })} />
      </Feld>
    </fieldset>
  );
}

/** Formularzeilen → API-Payload: leere Strings weglassen, Dezimalwerte normalisieren. */
function rowsToPayload(rows: MatrixRowForm[]) {
  return rows.map((r) => ({
    faktor: r.faktor,
    feststellung: r.feststellung || undefined,
    ausgangswert: r.ausgangswert || undefined,
    vergleichsdaten: r.vergleichsdaten || undefined,
    unterschiede: r.unterschiede || undefined,
    zuAbschlaege: r.zuAbschlaege || undefined,
    abschlag: parseDecimalInput(r.abschlag) ?? r.abschlag,
    beleg: r.beleg || undefined,
    gewicht: parseDecimalInput(r.gewicht) ?? r.gewicht,
    belegqualitaet: r.belegqualitaet || undefined,
    unsicherheitswirkung: r.unsicherheitswirkung || undefined,
    begruendung: r.begruendung || undefined,
    abschlagMin: r.abschlagMin.trim() ? (parseDecimalInput(r.abschlagMin) ?? r.abschlagMin) : undefined,
    abschlagMax: r.abschlagMax.trim() ? (parseDecimalInput(r.abschlagMax) ?? r.abschlagMax) : undefined,
    refId: r.refId || undefined,
    refDataClass: r.refDataClass || undefined,
  }));
}

function QKarte({
  caseId,
  data,
  reload,
  readonly,
  vm,
}: Pick<StepProps, 'caseId' | 'data' | 'reload' | 'readonly'> & { vm: any }) {
  const actions = useEntityActions(caseId, 'q-determinations', reload);
  const [form, setForm] = useState(leer());

  function leer() {
    return {
      label: '',
      evidenceLevel: '' as string,
      value: '',
      vIst: '',
      vSoll: '',
      linearTransferOptIn: false,
      justification: '',
      sourceRef: '',
      calibrationRef: '',
      matrix: [] as MatrixRowForm[],
      overlapNote: '',
      valuationDate: '',
      stateDefinition: '',
      uncertainty: '',
      assessorQualification: '',
      rMassnahme: '',
      rKosten: '',
      rErgebnis: '',
      rRestmangel: '',
      rZustand: '',
      rVRest: '',
      // K2 (D-031): gesonderte Restmangel-Matrix (Zustand nach Massnahme)
      rMatrix: [] as MatrixRowForm[],
    };
  }

  const level = form.evidenceLevel;
  const usesMatrix = level === 'A' || level === 'B' || level === 'C';

  // Live-Vorschau (Anzeige): q = 1 − Σ(Gewicht/100 × Abschlag). Verbindlich rechnet der Server.
  const gewichtSum = form.matrix.reduce((a, r) => a + (Number(r.gewicht) || 0), 0);
  const gesamtabschlag = form.matrix.reduce(
    (a, r) => a + ((Number(r.gewicht) || 0) / 100) * (Number(r.abschlag) || 0),
    0,
  );
  const qLive = 1 - gesamtabschlag;
  const vSollNum = Number(parseDecimalInput(form.vSoll) ?? '');
  const vIstLive = Number.isFinite(vSollNum) && form.vSoll ? vSollNum * qLive : null;
  // K4a: Live-Bandvorschau (nur wenn alle Zeilen beide Grenzen tragen)
  const bandLive = liveBand(form.matrix);
  // K2: Live-Vorschau q_rest / V_nachbesserung aus der Restmangel-Matrix
  const rGesamt = form.rMatrix.reduce(
    (a, r) => a + ((Number(r.gewicht) || 0) / 100) * (Number(r.abschlag) || 0),
    0,
  );
  const rGewichtSum = form.rMatrix.reduce((a, r) => a + (Number(r.gewicht) || 0), 0);
  const qRestLive = form.rMatrix.length ? 1 - rGesamt : null;
  const vRestLive =
    qRestLive !== null && Number.isFinite(vSollNum) && form.vSoll ? vSollNum * qRestLive : null;

  function setRow(i: number, patch: Partial<MatrixRowForm>) {
    setForm((f) => ({ ...f, matrix: f.matrix.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  }
  function addRow() {
    setForm((f) => ({ ...f, matrix: [...f.matrix, leerRow()] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({ ...f, matrix: f.matrix.filter((_, j) => j !== i) }));
  }
  function rSetRow(i: number, patch: Partial<MatrixRowForm>) {
    setForm((f) => ({ ...f, rMatrix: f.rMatrix.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  }
  function rAddRow() {
    setForm((f) => ({ ...f, rMatrix: [...f.rMatrix, leerRow()] }));
  }
  function rRemoveRow(i: number) {
    setForm((f) => ({ ...f, rMatrix: f.rMatrix.filter((_, j) => j !== i) }));
  }

  // Referenzfall-Vorschläge (D-032, «WerkDelta-Referenzpool»): belegte Vergleichsfälle als
  // VORSCHLAG; Übernahme legt eine Matrixzeile an, die gutachterlich zu vervollständigen
  // und zu bestätigen ist (keine automatische q-Setzung — Sperre 15.1).
  const [refMarkt, setRefMarkt] = useState('CH');
  const [refTemplateKey, setRefTemplateKey] = useState('fassade-oberflaeche-ch');
  const [refTreffer, setRefTreffer] = useState<any[] | null>(null);
  // Bewertungskontext des Falls (D-053): steuert nur die Sortierung und die
  // Kennzeichnung der Vorschläge. Abweichende Vergleichsfälle werden bewusst NICHT
  // ausgeblendet — verborgene Information wäre schlechter als eine sichtbare Abweichung.
  const fallExposition: string =
    (data.buildingElements ?? []).find((el: any) => el.expositionsgrad)?.expositionsgrad ?? '';
  const fallErwartung: string =
    (data.buildingObjects ?? []).find((o: any) => o.erwartungsklasse)?.erwartungsklasse ?? '';
  async function ladeVorschlaege() {
    try {
      const q = new URLSearchParams({ markt: refMarkt, templateKey: refTemplateKey });
      if (fallExposition) q.set('expositionsgrad', fallExposition);
      if (fallErwartung) q.set('erwartungsklasse', fallErwartung);
      const r = await api.get<any[]>(`/api/v1/referenzfaelle/vorschlaege?${q.toString()}`);
      setRefTreffer(r);
    } catch {
      setRefTreffer([]);
    }
  }
  function uebernehmen(rf: any) {
    setForm((f) => ({
      ...f,
      matrix: [
        ...f.matrix,
        {
          ...leerRow(),
          faktor: rf.faktor,
          abschlag: String(rf.abschlag ?? ''),
          abschlagMin: rf.abschlagMin != null ? String(rf.abschlagMin) : '',
          abschlagMax: rf.abschlagMax != null ? String(rf.abschlagMax) : '',
          refId: rf.id ?? '',
          refDataClass: rf.dataClass ?? '',
          ausgangswert:
            (rf.methodSource ? `methodSource: ${rf.methodSource}. ` : '') +
            (rf.numericSource ? `numericSource: ${rf.numericSource}. ` : rf.herkunft ?? '') +
            (rf.anpassungsregel ? `· Anpassungsregel: ${rf.anpassungsregel}` : ''),
          beleg: rf.herkunft ?? '',
          belegqualitaet: rf.isDemo || rf.dataClass === 'DEMO' ? 'DEMO-Referenzfall' : 'Referenzfall (freigegeben)',
          begruendung: rf.beschreibung ?? '',
        },
      ],
    }));
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    const value = form.value ? parseDecimalInput(form.value) : undefined;
    const vIst = form.vIst ? parseDecimalInput(form.vIst) : undefined;
    const vSoll = form.vSoll ? parseDecimalInput(form.vSoll) : undefined;
    const matrix = usesMatrix && form.matrix.length ? rowsToPayload(form.matrix) : undefined;
    const hasRMatrix = form.rMatrix.length > 0;
    const remediation =
      [form.rMassnahme, form.rKosten, form.rErgebnis, form.rRestmangel, form.rZustand, form.rVRest].some(
        Boolean,
      ) || hasRMatrix
        ? {
            massnahme: form.rMassnahme || undefined,
            kosten: form.rKosten || undefined,
            ergebnis: form.rErgebnis || undefined,
            restmangel: form.rRestmangel || undefined,
            zustand: form.rZustand || undefined,
            // Mit Restmangel-Matrix wird V_nachbesserung serverseitig hergeleitet
            // (schreibgeschützt); eine manuelle Eingabe wird dann nicht gesendet.
            vRest: hasRMatrix
              ? undefined
              : form.rVRest
                ? (parseDecimalInput(form.rVRest) ?? undefined)
                : undefined,
            matrix: hasRMatrix ? rowsToPayload(form.rMatrix) : undefined,
          }
        : undefined;
    const ok = await actions.create({
      valuationModelId: vm.id,
      label: form.label,
      evidenceLevel: form.evidenceLevel,
      // A–C: q ist schreibgeschützt und wird serverseitig aus der Matrix hergeleitet.
      value: usesMatrix ? undefined : (value ?? undefined),
      vIst: usesMatrix ? undefined : (vIst ?? undefined),
      vSoll: vSoll ?? undefined,
      linearTransferOptIn: form.linearTransferOptIn,
      justification: form.justification,
      sourceRef: form.sourceRef || undefined,
      calibrationRef: form.calibrationRef || undefined,
      derivationMatrix: matrix,
      overlapNote: form.overlapNote || undefined,
      valuationDate: form.valuationDate || undefined,
      stateDefinition: form.stateDefinition || undefined,
      uncertainty: form.uncertainty || undefined,
      assessorQualification: form.assessorQualification || undefined,
      remediationScenario: remediation,
    });
    if (ok) setForm(leer());
  }

  const qList = (vm.qDeterminations ?? []) as any[];

  return (
    <section className="karte">
      <h2>
        {t('step8.qTitle')}
        <InfoTip termKey="wertrelation" />
        <InfoTip termKey="evidenzstufe" />
      </h2>
      <p className="erklaerung">{t('step8.qIntro')}</p>
      {qList.length === 0 && <div className="leer-hinweis">{t('step8.qEmpty')}</div>}
      {qList.map((q) => (
        <div className="eintrag" key={q.id}>
          <div className="eintrag-kopf">
            <strong>{q.label}</strong>
            <span className={`badge ${q.evidenceLevel === 'D' || q.evidenceLevel === 'E' ? 'warn' : 'tuerkis'}`}>
              {t(`evidence.level.${q.evidenceLevel}`)}
            </span>
            {!readonly && (
              <button type="button" className="knopf-sekundaer knopf-klein" onClick={() => actions.remove(q.id)}>
                {t('common.delete')}
              </button>
            )}
          </div>
          <p className="meta">
            {q.value !== null && q.value !== undefined ? `q = ${q.value}` : ''}
            {q.vIst && q.vSoll ? `V_ist ${formatCHF(q.vIst)} / V_soll ${formatCHF(q.vSoll)}` : ''}
            {q.sourceRef ? ` · ${t('common.source')}: ${q.sourceRef}` : ''}
            {q.calibrationRef ? ` · ${t('step8.calibration')}: ${q.calibrationRef}` : ''}
          </p>
          <p className="meta">{q.justification}</p>
          {q.evidenceLevel === 'D' && <RechtsHinweisFrei text={t('calc.warning.W_Q_LINEAR_TRANSFER')} />}
          {q.evidenceLevel === 'E' && (
            <div className="warnung">{t('calc.warning.W_Q_NO_DOCUMENTED_BASIS')}</div>
          )}
        </div>
      ))}
      {qList.length > 0 && (
        <p className="meta">
          {t('step8.qAuditNote')}{' '}
          <Link to={`/faelle/${caseId}/audit`}>{t('step8.qAuditLink')}</Link>
        </p>
      )}
      {!readonly && (
        <form onSubmit={add}>
          <h3>{t('step8.addQ')}</h3>
          <div className="feld-zeile">
            <Feld label={t('step8.qLabel')} required error={actions.fieldErrors['label']}>
              <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required />
            </Feld>
            <Feld label={t('step8.evidenceLevel')} required hint={t('step8.evidenceLevelHint')}>
              <select
                value={form.evidenceLevel}
                onChange={(e) =>
                  setForm({ ...leer(), label: form.label, justification: form.justification, evidenceLevel: e.target.value })
                }
                required
              >
                <option value="">{t('step8.evidenceLevelChoose')}</option>
                {EVIDENCE_LEVELS.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {t(`evidence.level.${lvl}`)}
                  </option>
                ))}
              </select>
            </Feld>
          </div>
          {usesMatrix && (
            <div className="matrix-editor">
              <p className="erklaerung">{t('step8.matrixIntro')}</p>
              <Feld label={t('step8.vSoll')} hint={t('input.decimalHint')} error={actions.fieldErrors['vSoll']}>
                <input value={form.vSoll} onChange={(e) => setForm({ ...form, vSoll: e.target.value })} />
              </Feld>
              {form.matrix.map((r, i) => (
                <MatrixZeile key={i} index={i} row={r} onPatch={(p) => setRow(i, p)} onRemove={() => removeRow(i)} />
              ))}
              <button type="button" className="knopf-sekundaer knopf-klein" onClick={addRow}>
                {t('step8.addRow')}
              </button>
              <p className="meta">{t('step8.bandHint')}</p>
              <details>
                <summary className="meta">{t('step8.refTitle')}</summary>
                <p className="erklaerung">{t('step8.refIntro')}</p>
                <div className="feld-zeile">
                  <Feld label={t('step8.refMarkt')}>
                    <select value={refMarkt} onChange={(e) => setRefMarkt(e.target.value)}>
                      <option value="CH">CH</option>
                      <option value="DE">DE</option>
                    </select>
                  </Feld>
                  <Feld label={t('step8.refTemplate')}>
                    <input value={refTemplateKey} onChange={(e) => setRefTemplateKey(e.target.value)} />
                  </Feld>
                  <button type="button" className="knopf-sekundaer knopf-klein" onClick={ladeVorschlaege}>
                    {t('step8.refLoad')}
                  </button>
                </div>
                {(fallExposition || fallErwartung) && (
                  <p className="meta">
                    {t('kontext.fallKontext', {
                      exposition: fallExposition
                        ? t(`kontext.exposition.${fallExposition}`)
                        : t('kontext.keine'),
                      erwartung: fallErwartung
                        ? t(`kontext.erwartung.${fallErwartung}`)
                        : t('kontext.keine'),
                    })}
                  </p>
                )}
                <p className="meta">{t('kontext.treffer.hint')}</p>
                {refTreffer && refTreffer.length === 0 && (
                  <p className="meta">{t('step8.refEmpty')}</p>
                )}
                {refTreffer?.map((rf) => (
                  <div className="eintrag" key={rf.id}>
                    <div className="eintrag-kopf">
                      <strong>{rf.faktor}</strong>
                      <span className="badge tuerkis">
                        {rf.abschlag}
                        {rf.abschlagMin != null ? ` (${rf.abschlagMin}–${rf.abschlagMax})` : ''}
                      </span>
                      {rf.isDemo && <span className="badge warn">DEMO</span>}
                      {rf.kontextTreffer && rf.kontextTreffer !== 'UNBEKANNT' && (
                        <span className={`badge ${rf.kontextTreffer === 'GENAU' ? '' : 'warn'}`}>
                          {t(`kontext.treffer.${rf.kontextTreffer}`)}
                        </span>
                      )}
                      <button
                        type="button"
                        className="knopf-sekundaer knopf-klein"
                        onClick={() => uebernehmen(rf)}
                      >
                        {t('step8.refUebernehmen')}
                      </button>
                    </div>
                    <p className="meta">
                      {rf.herkunft}
                      {rf.anpassungsregel ? ` · ${rf.anpassungsregel}` : ''}
                    </p>
                  </div>
                ))}
              </details>
              <div
                className={form.matrix.length > 0 && gewichtSum !== 100 ? 'warnung' : ''}
                style={{ marginTop: '0.6rem', padding: '0.5rem 0.75rem', border: '1px solid #cdd9e1', borderRadius: '4px' }}
              >
                <p>
                  <strong>
                    {t('report.matrix.gesamt')}: {(gesamtabschlag * 100).toFixed(1)} % ·{' '}
                    {t('report.matrix.qResult')} = {qLive.toFixed(4)}
                  </strong>{' '}
                  <span className="meta">({t('step8.qComputed')})</span>
                </p>
                {form.matrix.length > 0 && gewichtSum !== 100 && (
                  <p className="feld-fehler">{t('step8.gewichtSum', { sum: String(gewichtSum) })}</p>
                )}
                {vIstLive !== null && (
                  <p className="meta">
                    {t('report.threeValues.vIst')}: {formatCHF(vIstLive.toFixed(2))} ({t('report.matrix.vIstDerived')})
                  </p>
                )}
                {bandLive && (
                  <p className="meta">
                    {t('step8.bandPreview', {
                      qMin: bandLive.qMin.toFixed(4),
                      qMax: bandLive.qMax.toFixed(4),
                    })}
                  </p>
                )}
              </div>
              <h4>{t('step8.metaTitle')}</h4>
              <div className="feld-zeile">
                <Feld label={t('report.matrix.stichtag')}>
                  <input type="date" value={form.valuationDate} onChange={(e) => setForm({ ...form, valuationDate: e.target.value })} />
                </Feld>
                <Feld label={t('report.matrix.qualifikation')}>
                  <input value={form.assessorQualification} onChange={(e) => setForm({ ...form, assessorQualification: e.target.value })} />
                </Feld>
              </div>
              <Feld label={t('report.matrix.zustand')}>
                <input value={form.stateDefinition} onChange={(e) => setForm({ ...form, stateDefinition: e.target.value })} />
              </Feld>
              <Feld label={t('report.matrix.overlap')}>
                <input value={form.overlapNote} onChange={(e) => setForm({ ...form, overlapNote: e.target.value })} />
              </Feld>
              <Feld label={t('report.matrix.uncertainty')}>
                <input value={form.uncertainty} onChange={(e) => setForm({ ...form, uncertainty: e.target.value })} />
              </Feld>
              <details>
                <summary className="meta">{t('step8.remediationTitle')}</summary>
                <Feld label={t('step8.rMassnahme')}>
                  <input value={form.rMassnahme} onChange={(e) => setForm({ ...form, rMassnahme: e.target.value })} />
                </Feld>
                <div className="feld-zeile">
                  <Feld label={t('step8.rKosten')}>
                    <input value={form.rKosten} onChange={(e) => setForm({ ...form, rKosten: e.target.value })} />
                  </Feld>
                  <Feld
                    label={t('step8.rVRest')}
                    hint={form.rMatrix.length ? undefined : t('input.decimalHint')}
                  >
                    {form.rMatrix.length ? (
                      <input value="" placeholder={t('step8.qComputed')} disabled />
                    ) : (
                      <input value={form.rVRest} onChange={(e) => setForm({ ...form, rVRest: e.target.value })} />
                    )}
                  </Feld>
                </div>
                {form.rMatrix.length > 0 && <p className="meta">{t('step8.rVRestComputed')}</p>}
                <Feld label={t('step8.rErgebnis')}>
                  <input value={form.rErgebnis} onChange={(e) => setForm({ ...form, rErgebnis: e.target.value })} />
                </Feld>
                <Feld label={t('step8.rRestmangel')}>
                  <input value={form.rRestmangel} onChange={(e) => setForm({ ...form, rRestmangel: e.target.value })} />
                </Feld>
                <Feld label={t('step8.rZustand')}>
                  <input value={form.rZustand} onChange={(e) => setForm({ ...form, rZustand: e.target.value })} />
                </Feld>
                <h4>{t('step8.rMatrixTitle')}</h4>
                <p className="erklaerung">{t('step8.rMatrixIntro')}</p>
                {form.rMatrix.map((r, i) => (
                  <MatrixZeile key={i} index={i} row={r} onPatch={(p) => rSetRow(i, p)} onRemove={() => rRemoveRow(i)} />
                ))}
                <button type="button" className="knopf-sekundaer knopf-klein" onClick={rAddRow}>
                  {t('step8.rMatrixAdd')}
                </button>
                {qRestLive !== null && (
                  <p className={rGewichtSum !== 100 ? 'feld-fehler' : 'meta'}>
                    {rGewichtSum !== 100
                      ? t('step8.gewichtSum', { sum: String(rGewichtSum) })
                      : t('step8.rQRestPreview', {
                          qRest: qRestLive.toFixed(4),
                          vRest: vRestLive !== null ? formatCHF(vRestLive.toFixed(2)) : '—',
                        })}
                  </p>
                )}
              </details>
            </div>
          )}
          {level === 'E' && (
            <Feld label={t('step8.qValue')} hint={t('step8.qValueHint')} error={actions.fieldErrors['value']}>
              <input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </Feld>
          )}
          {(level === 'A' || level === 'B') && (
            <Feld label={t('common.source')} required error={actions.fieldErrors['sourceRef']}>
              <input value={form.sourceRef} onChange={(e) => setForm({ ...form, sourceRef: e.target.value })} />
            </Feld>
          )}
          {level === 'C' && (
            <Feld label={t('step8.calibration')} required error={actions.fieldErrors['calibrationRef']}>
              <input
                value={form.calibrationRef}
                onChange={(e) => setForm({ ...form, calibrationRef: e.target.value })}
              />
            </Feld>
          )}
          {level === 'D' && (
            <div className="warnung">
              <div className="kontrollzeile">
                <input
                  id="optin-d"
                  type="checkbox"
                  checked={form.linearTransferOptIn}
                  onChange={(e) => setForm({ ...form, linearTransferOptIn: e.target.checked })}
                />
                <label htmlFor="optin-d">{t('evidence.level.D.optin')}</label>
              </div>
              {actions.fieldErrors['linearTransferOptIn'] && (
                <p className="feld-fehler">{actions.fieldErrors['linearTransferOptIn']}</p>
              )}
            </div>
          )}
          {level === 'E' && <div className="warnung">{t('calc.warning.W_Q_NO_DOCUMENTED_BASIS')}</div>}
          <Feld
            label={t('common.justification')}
            required
            error={actions.fieldErrors['justification']}
          >
            <textarea
              value={form.justification}
              onChange={(e) => setForm({ ...form, justification: e.target.value })}
              required
            />
          </Feld>
          <Fehler code={actions.errorCode} />
          <div className="aktionen">
            <button type="submit" className="knopf-primaer" disabled={!level}>
              {t('common.add')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function RechtsHinweisFrei({ text }: { text: string }) {
  return <div className="warnung">{text}</div>;
}

/**
 * Vergleichende Darstellung mehrerer q-Szenarien (Auftrag 3.4).
 * Reine Vorschau aus dem Rechenkern — verbindlich bleibt die gespeicherte
 * Berechnung mit Snapshot und Prüfsumme.
 */
function QVergleichKarte({ caseId, vm }: { caseId: string; vm: any }) {
  const [comparison, setComparison] = useState<any | null>(null);
  const qCount = (vm.qDeterminations ?? []).length;

  useEffect(() => {
    if (qCount < 2) {
      setComparison(null);
      return;
    }
    api
      .get(`/api/v1/cases/${caseId}/valuation-models/${vm.id}/q-comparison`)
      .then(setComparison)
      .catch(() => setComparison(null));
  }, [caseId, vm.id, qCount]);

  if (qCount < 2 || !comparison) return null;

  return (
    <section className="karte">
      <h2>{t('step8.qCompareTitle')}</h2>
      <p className="erklaerung">{t('step8.qCompareIntro')}</p>
      <div className="tabelle-scroll">
      <table className="daten" style={{ maxWidth: '58rem' }}>
        <thead>
          <tr>
            <th>{t('step8.qLabel')}</th>
            <th>{t('step8.evidenceLevel')}</th>
            <th className="zahl">{t('step8.resultQ')}</th>
            <th className="zahl">{t('step8.resultM')}</th>
            <th>{t('common.status')}</th>
          </tr>
        </thead>
        <tbody>
          {comparison.rows.map((row: any) => (
            <tr key={row.qDeterminationId}>
              <td>{row.label}</td>
              <td>
                <span
                  className={`badge ${row.evidenceLevel === 'D' || row.evidenceLevel === 'E' ? 'warn' : 'tuerkis'}`}
                >
                  {row.evidenceLevel}
                </span>
              </td>
              <td className="zahl">{row.q ?? '—'}</td>
              <td className="zahl">
                {row.reductionAmount ? <strong>{formatCHF(row.reductionAmount)}</strong> : '—'}
              </td>
              <td>
                {row.blocked?.length > 0
                  ? t(`calc.blocked.${row.blocked[0].code}`, row.blocked[0].params)
                  : row.errors?.length > 0
                    ? t(`calc.error.${row.errors[0].code}`, row.errors[0].params)
                    : row.warnings?.length > 0
                      ? t(`calc.warning.${row.warnings[0].code}`, row.warnings[0].params)
                      : t('step8.qCompareOk')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <p className="meta">{t('step8.qCompareNote')}</p>
    </section>
  );
}

/* ————— Berechnung ————— */

function BerechnungKarte({
  caseId,
  readonly,
  vm,
  reload,
}: {
  caseId: string;
  readonly: boolean;
  vm: any;
  reload: () => void;
}) {
  const [qId, setQId] = useState('');
  const [assessmentId, setAssessmentId] = useState('');
  const [withLifespan, setWithLifespan] = useState(false);
  const [lifespan, setLifespan] = useState({
    replacementCost: '',
    replacementCostSource: '',
    discountRate: '',
    discountRateSource: '',
    yearsDefective: '',
    yearsSound: '',
    yearsSource: '',
  });
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Ergebnis nur beim Wechsel des Bewertungsauftrags zurücksetzen —
  // nicht bei jedem Daten-Reload (der nach der Berechnung selbst passiert).
  useEffect(() => {
    setResult(null);
    setError(null);
  }, [vm.id]);

  useEffect(() => {
    setQId((prev) =>
      prev && vm.qDeterminations?.some((q: any) => q.id === prev)
        ? prev
        : (vm.qDeterminations?.[0]?.id ?? ''),
    );
    setAssessmentId((prev) =>
      prev && vm.assessments?.some((a: any) => a.id === prev)
        ? prev
        : (vm.assessments?.[0]?.id ?? ''),
    );
  }, [vm.id, vm.qDeterminations, vm.assessments]);

  async function run() {
    setError(null);
    setBusy(true);
    setResult(null);
    try {
      const body: Record<string, unknown> = {};
      if (qId) body.qDeterminationId = qId;
      if (assessmentId) body.assessmentId = assessmentId;
      if (withLifespan) {
        const parsed = {
          replacementCost: parseDecimalInput(lifespan.replacementCost) ?? '',
          replacementCostSource: lifespan.replacementCostSource,
          discountRate: parseDecimalInput(lifespan.discountRate) ?? '',
          discountRateSource: lifespan.discountRateSource,
          yearsDefective: parseDecimalInput(lifespan.yearsDefective) ?? '',
          yearsSound: parseDecimalInput(lifespan.yearsSound) ?? '',
          yearsSource: lifespan.yearsSource,
        };
        body.lifespan = parsed;
      }
      const res = await api.post(
        `/api/v1/cases/${caseId}/valuation-models/${vm.id}/calculations`,
        body,
      );
      setResult(res);
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'INTERNAL');
    } finally {
      setBusy(false);
    }
  }

  const r = result?.result;

  return (
    <section className="karte">
      <h2>{t('step8.calcTitle')}</h2>
      <p className="erklaerung">{t('step8.calcIntro')}</p>
      <div className="feld-zeile">
        <Feld label={t('step8.qSelect')} required>
          <select value={qId} onChange={(e) => setQId(e.target.value)}>
            <option value="">—</option>
            {(vm.qDeterminations ?? []).map((q: any) => (
              <option key={q.id} value={q.id}>
                {q.label} ({t(`evidence.level.${q.evidenceLevel}`)})
              </option>
            ))}
          </select>
        </Feld>
        <Feld label={t('step8.assessmentSelect')}>
          <select value={assessmentId} onChange={(e) => setAssessmentId(e.target.value)}>
            <option value="">—</option>
            {(vm.assessments ?? []).map((a: any) => (
              <option key={a.id} value={a.id}>
                {t(`strand.${a.strand}`)} ·{' '}
                {a.status === 'ABGESCHLOSSEN' ? t('step8.completed') : t('step8.draft')}
              </option>
            ))}
          </select>
        </Feld>
      </div>

      <div className="kontrollzeile">
        <input
          id="with-lifespan"
          type="checkbox"
          checked={withLifespan}
          onChange={(e) => setWithLifespan(e.target.checked)}
        />
        <label htmlFor="with-lifespan">{t('step8.withLifespan')}</label>
      </div>
      {withLifespan && (
        <fieldset>
          <legend>{t('step8.lifespanLegend')}</legend>
          <div className="feld-zeile">
            <Feld label={t('step8.replacementCost')} required hint={t('input.decimalHint')}>
              <input
                value={lifespan.replacementCost}
                onChange={(e) => setLifespan({ ...lifespan, replacementCost: e.target.value })}
              />
            </Feld>
            <Feld label={t('step8.replacementCostSource')} required>
              <input
                value={lifespan.replacementCostSource}
                onChange={(e) => setLifespan({ ...lifespan, replacementCostSource: e.target.value })}
              />
            </Feld>
          </div>
          <div className="feld-zeile">
            <Feld label={t('step8.discountRate')} required hint={t('step8.discountRateHint')}>
              <input
                value={lifespan.discountRate}
                onChange={(e) => setLifespan({ ...lifespan, discountRate: e.target.value })}
              />
            </Feld>
            <Feld label={t('step8.discountRateSource')} required>
              <input
                value={lifespan.discountRateSource}
                onChange={(e) => setLifespan({ ...lifespan, discountRateSource: e.target.value })}
              />
            </Feld>
          </div>
          <div className="feld-zeile">
            <Feld label={t('step8.yearsDefective')} required>
              <input
                value={lifespan.yearsDefective}
                onChange={(e) => setLifespan({ ...lifespan, yearsDefective: e.target.value })}
              />
            </Feld>
            <Feld label={t('step8.yearsSound')} required>
              <input
                value={lifespan.yearsSound}
                onChange={(e) => setLifespan({ ...lifespan, yearsSound: e.target.value })}
              />
            </Feld>
            <Feld label={t('step8.yearsSource')} required>
              <input
                value={lifespan.yearsSource}
                onChange={(e) => setLifespan({ ...lifespan, yearsSource: e.target.value })}
              />
            </Feld>
          </div>
          <p className="hinweis">{t('step8.lifespanStatusNote')}</p>
        </fieldset>
      )}

      {!readonly && (
        <div className="aktionen">
          <button type="button" className="knopf-primaer" onClick={run} disabled={busy || !qId}>
            {t('step8.runCalculation')}
          </button>
        </div>
      )}

      <Fehler code={error} />
      {r && (
        <div style={{ marginTop: '1rem' }}>
          <h3>{t('step8.resultTitle')}</h3>
          {r.blocked?.map((b: any, i: number) => (
            <div className="fehlermeldung" key={i} role="alert">
              {t(`calc.blocked.${b.code}`, b.params)}
            </div>
          ))}
          {r.errors?.map((err: any, i: number) => (
            <div className="fehlermeldung" key={i} role="alert">
              {t(`calc.error.${err.code}`, err.params)}
            </div>
          ))}
          {r.warnings?.map((w: any, i: number) => (
            <div className="warnung" key={i}>
              {t(`calc.warning.${w.code}`, w.params)}
            </div>
          ))}
          {r.hints?.map((h: any, i: number) => (
            <RechtsHinweis key={i} text={t(`calc.hint.${h.code}`, h.params)} />
          ))}
          <div className="tabelle-scroll">
          <table className="daten" style={{ maxWidth: '46rem' }}>
            <tbody>
              {r.u && (
                <tr>
                  <th>{t('step8.resultU')}</th>
                  <td className="zahl">{r.u.u}</td>
                </tr>
              )}
              {r.q && (
                <tr>
                  <th>
                    {t('step8.resultQ')} ({t(`evidence.level.${r.q.evidenceLevel}`)})
                  </th>
                  <td className="zahl">{r.q.value}</td>
                </tr>
              )}
              {r.reduction && (
                <>
                  <tr>
                    <th>{t('step8.resultP')}</th>
                    <td className="zahl">{formatCHF(r.reduction.price)}</td>
                  </tr>
                  <tr>
                    <th>
                      <strong>{t('step8.resultM')}</strong>
                    </th>
                    <td className="zahl">
                      <strong>{formatCHF(r.reduction.reductionAmount)}</strong>
                    </td>
                  </tr>
                  <tr>
                    <th>{t('step8.resultPReduced')}</th>
                    <td className="zahl">{formatCHF(r.reduction.priceReduced)}</td>
                  </tr>
                </>
              )}
              {r.repairComparison?.ratioReductionToRepair && (
                <tr>
                  <th>{t('step8.resultRatio')}</th>
                  <td className="zahl">{r.repairComparison.ratioReductionToRepair}</td>
                </tr>
              )}
              {r.lifespan && (
                <tr>
                  <th>{t('step8.resultLifespan')}</th>
                  <td className="zahl">{formatCHF(r.lifespan.delta)}</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          <p className="meta">
            {t('step8.snapshotInfo', {
              version: r.calcCoreVersion,
              hash: result.snapshotSha256.slice(0, 16),
            })}
          </p>
        </div>
      )}
    </section>
  );
}
